import React from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { gridFormatters, gridValueGetters } from 'src/utils/grid'
import { ContractorLastActivity } from 'src/components/contractor-list/shared/ContractorLastActivity'
import { Contractor, ContractorWithDeactivationDetails } from 'src/interfaces/contractor'
import { ContractorStatusChip } from 'src/components/contractor-list/shared/ContractorStatusChip'
import { TableTags } from 'src/components/tags/TableTags'
import { ContractorsTableFields, ContractorState, ContractorStatus } from './enums'
import {
  contractorsTableHeaderTitles,
  contractorStatusTitles,
  contractorTypeTitles,
} from './constants'
import { ContractorTypeChip } from 'src/components/contractor-list/shared/ContractorTypeChip'
import { VehicleTypeChip } from 'src/components/contractor-list/shared/VehicleTypeChip'
import { vehicleNamesMap } from 'src/constants/vehicle'
import { Chip } from 'src/components/shared/Chip'
import { addS } from 'src/utils/baseUtils'
import { colors } from 'src/theme'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { getOnboardingTableStepTitle } from 'src/utils/collectedData'
import { LimitedStringChip } from 'src/components/shared/LimitedStringChip'
import { RecollectionIndicator } from 'src/components/contractor-list/shared/recollection-indicator'
import { ExpirationIndicatorChip } from 'src/components/contractor-list/shared/ExpirationIndicatorChip'
import { ContractorReActivationChip } from 'src/components/contractor-list/shared/ContractorReActivationChip'

const needsReviewColumn: GridColDef = {
  field: ContractorsTableFields.toReviewCount,
  headerName: contractorsTableHeaderTitles[ContractorsTableFields.toReviewCount],
  flex: 2,
  minWidth: 120,
  sortable: true,
  renderCell: (params: GridRenderCellParams<Contractor>) =>
    params.value > 0 ? (
      <Tooltip
        title={params.row.documentsToReview.map(getOnboardingTableStepTitle).join(', ')}
        placement="bottom-start"
      >
        <span>
          <Chip
            text={`${params.value} ${addS(Number(params.value), 'step')}`}
            textColor={colors.COLORS_ORANGE_500}
            className={tw`bg-[rgba(255, 238, 214, 1)]`}
          />
        </span>
      </Tooltip>
    ) : null,
}

const sharedContractorColumns: Array<GridColDef> = [
  {
    field: ContractorsTableFields.externalId,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.externalId],
    flex: 2,
    minWidth: 120,
    sortable: true,
  },
  {
    field: ContractorsTableFields.name,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.name],
    flex: 2,
    minWidth: 140,
    sortable: true,
  },
  {
    field: ContractorsTableFields.contractorType,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.contractorType],
    flex: 2,
    minWidth: 84,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) =>
      contractorTypeTitles[params.row.contractorType],
    renderCell: (params: GridRenderCellParams<Contractor>) => {
      return <ContractorTypeChip type={params.row.contractorType} />
    },
  },
  {
    field: ContractorsTableFields.vehicleType,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.vehicleType],
    flex: 2,
    minWidth: 130,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) => {
      return params.row.vehicleType ? vehicleNamesMap[params.row.vehicleType] : '-'
    },
    renderCell: (params: GridRenderCellParams<Contractor>) => {
      return <VehicleTypeChip type={params.row.vehicleType} />
    },
  },
]

const statusCol = {
  field: ContractorsTableFields.status,
  headerName: contractorsTableHeaderTitles[ContractorsTableFields.status],
  flex: 2,
  minWidth: 120,
  sortable: true,
  valueGetter: (params: GridValueGetterParams<Contractor>) =>
    contractorStatusTitles[params.row.status],
  renderCell: (params: GridRenderCellParams<Contractor>) => {
    return <ContractorStatusChip status={params.row.status} />
  },
}

const deactivationDetailsCol: GridColDef = {
  field: ContractorsTableFields.deactivationDetails,
  headerName: contractorsTableHeaderTitles[ContractorsTableFields.deactivationDetails],
  flex: 3,
  sortable: false,
  renderCell: (params: GridRenderCellParams<ContractorWithDeactivationDetails>) => {
    const { deactivationReason, deactivatedByEmail, dateDeactivated, deactivationInfo } = params.row

    return (
      <ContractorStatusChip
        status={ContractorStatus.Deactivated}
        deactivationReason={deactivationReason}
        deactivatedByEmail={deactivatedByEmail}
        deactivatedAt={dateDeactivated}
        expiredSteps={[deactivationInfo?.stepType]}
      />
    )
  },
}

const lastColumns = [
  {
    field: ContractorsTableFields.onboardingFlow,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.onboardingFlow],
    flex: 2,
    minWidth: 140,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Contractor>) =>
      params.value ? <LimitedStringChip text={params.value} /> : null,
  },
  needsReviewColumn,
  {
    field: ContractorsTableFields.phone,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.phone],
    flex: 2,
    minWidth: 140,
    sortable: true,
  },
  {
    field: ContractorsTableFields.email,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.email],
    flex: 3,
    minWidth: 140,
    sortable: true,
  },
  {
    field: ContractorsTableFields.tags,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.tags],
    flex: 3,
    minWidth: 120,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<Contractor>) => params.row?.tags.join(', '),
    renderCell: (params: GridRenderCellParams<Contractor>) =>
      params.row?.tags.length ? (
        <TableTags tags={params.row?.tags || []} cutLength={5} tooltipThreshold={5} />
      ) : (
        <span>-</span>
      ),
  },
  {
    field: ContractorsTableFields.lastActivity,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.lastActivity],
    flex: 2,
    minWidth: 120,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<Contractor>) => {
      return params.row.lastActivity?.createdAt
        ? dayjs(params.row.lastActivity.createdAt).toDate()
        : null
    },
    renderCell: (params: GridRenderCellParams<Contractor>) => (
      <ContractorLastActivity lastActivity={params.row.lastActivity} />
    ),
  },
  {
    field: ContractorsTableFields.referral,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.referral],
    flex: 2,
    minWidth: 120,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) => params.value || '-',
  },
]

const mcCol = [
  {
    field: ContractorsTableFields.masterContractorName,
    headerName: contractorsTableHeaderTitles[ContractorsTableFields.masterContractorName],
    flex: 3,
    minWidth: 140,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) => params.value || '-',
  },
]

const expiresInColumn: GridColDef = {
  field: ContractorsTableFields.expiresInDays,
  headerName: contractorsTableHeaderTitles[ContractorsTableFields.expiresInDays],
  flex: 2,
  minWidth: 120,
  sortable: true,
  renderCell: (params: GridRenderCellParams<Contractor>) => {
    return (
      <ExpirationIndicatorChip
        expiresInDays={params.row.expiresInDays}
        expirationIndicator={params.row.expirationIndicator}
      />
    )
  },
}

const recollectionColumn: GridColDef = {
  field: ContractorsTableFields.recollections,
  headerName: contractorsTableHeaderTitles[ContractorsTableFields.recollections],
  flex: 2,
  minWidth: 120,
  sortable: false,
  valueGetter: (params: GridValueGetterParams<Contractor>) => {
    const recollections = params.row.recollections

    if (!recollections?.length) {
      return ''
    }

    const uniqueTypes = _.uniq(
      recollections.map((recollection) => {
        return recollection.type
      }),
    )

    return uniqueTypes.join(', ')
  },
  renderCell: (params: GridRenderCellParams<Contractor>) => {
    const recollections = params.row.recollections

    if (!recollections?.length) {
      return null
    }

    return <RecollectionIndicator recollections={recollections} muted />
  },
}

const reActivationColumn: GridColDef = {
  field: ContractorsTableFields.autoActivationEnabled,
  headerName: contractorsTableHeaderTitles[ContractorsTableFields.autoActivationEnabled],
  flex: 2,
  minWidth: 120,
  sortable: false,
  valueGetter: (params: GridValueGetterParams<Contractor>) =>
    params.row.autoActivationEnabled ? 'Enabled' : 'Disabled',
  renderCell: (params: GridRenderCellParams<Contractor>) => (
    <ContractorReActivationChip isEnabled={!!params.row.autoActivationEnabled} />
  ),
}

export const contractorColumns = {
  [ContractorState.Active]: [
    ...sharedContractorColumns,
    expiresInColumn,
    recollectionColumn,
    ...lastColumns,
    {
      field: ContractorsTableFields.dateOnboarded,
      headerName: contractorsTableHeaderTitles[ContractorsTableFields.dateOnboarded],
      flex: 2,
      minWidth: 140,
      sortable: true,
      valueGetter: gridValueGetters.parseDate,
      valueFormatter: gridFormatters.formatShortDate,
    },
    ...mcCol,
    reActivationColumn,
  ],
  [ContractorState.Deactivated]: [
    ...sharedContractorColumns,
    deactivationDetailsCol,
    ...lastColumns,
    {
      field: ContractorsTableFields.dateDeactivated,
      headerName: contractorsTableHeaderTitles[ContractorsTableFields.dateDeactivated],
      flex: 2,
      sortable: true,
      valueGetter: gridValueGetters.parseDate,
      valueFormatter: gridFormatters.formatShortDate,
    },
    ...mcCol,
    reActivationColumn,
  ],
  [ContractorState.Recollection]: [
    ...sharedContractorColumns,
    statusCol,
    ...lastColumns,
    ...mcCol,
    reActivationColumn,
  ],
}

export const contractorInitialSortFields = {
  [ContractorState.Active]: 'dateOnboarded',
  [ContractorState.Deactivated]: 'dateDeactivated',
  [ContractorState.Recollection]: undefined,
}

export const contractorPageSizeOptions = [100, 250, 500, 1000]
